import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiSun, FiMoon } from 'react-icons/fi';
import {
  useColorMode,
  Box,
  Button,
  Flex,
  useColorModeValue,
  Link,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../../Asset/logo.png';

const MotionBox = motion(Box);

// --- Sub-component: NavLink ---
const NavLink = ({ to, children, onClick, isSubItem = false, ...props }) => {
  const brandBlue = '#274559';
  const linkColor = useColorModeValue(brandBlue, 'gray.300');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');
  const hoverColor = useColorModeValue('blue.600', 'white');

  return (
    <Link 
      as={RouterLink} 
      to={to} 
      onClick={onClick} 
      style={{ textDecoration: 'none' }}
      {...props}
    >
      <Button
        variant="ghost"
        color={linkColor}
        borderRadius="xl"
        fontWeight={isSubItem ? "normal" : "bold"}
        fontSize={isSubItem ? "sm" : "sm"}
        _hover={{ bg: hoverBg, color: hoverColor }}
        width={{ base: 'full', lg: 'auto' }}
        justifyContent={{ base: 'flex-start', lg: 'center' }}
        py={{ base: 6, lg: 2 }}
        px={{ base: 4, lg: 3 }}
      >
        {children}
      </Button>
    </Link>
  );
};

// --- Sub-component: DropdownMenu (Desktop) ---
const DropdownMenu = ({ title, children, isOpen, onToggle }) => {
  const brandBlue = '#274559';
  const menuBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const linkColor = useColorModeValue(brandBlue, 'gray.300');
  const hoverBg = useColorModeValue('blue.50', 'gray.700');

  return (
    <Box position="relative">
      <Button
        variant="ghost"
        onClick={onToggle}
        rightIcon={<FiChevronDown />}
        color={linkColor}
        fontWeight="bold"
        borderRadius="full"
        _hover={{ bg: hoverBg }}
      >
        {title}
      </Button>
      <AnimatePresence>
        {isOpen && (
          <MotionBox
            key="dropdown-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            position="absolute"
            top="120%"
            left="50%"
            transform="translateX(-50%)"
            bg={menuBg}
            boxShadow="2xl"
            borderRadius="xl"
            border="1px solid"
            borderColor={borderColor}
            p={2}
            minW="220px"
            zIndex="1100"
          >
            <VStack align="stretch" spacing={1}>
              {children}
            </VStack>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

// --- Sub-component: AccordionMenu (Mobile) ---
const AccordionMenu = ({ title, items, closeAllMenus }) => {
  const [isOpen, setIsOpen] = useState(false);
  const brandBlue = '#274559';
  const linkColor = useColorModeValue(brandBlue, 'gray.300');
  const borderColor = useColorModeValue('blue.100', 'gray.600');

  return (
    <Box w="full">
      <Button
        variant="ghost"
        w="full"
        justifyContent="space-between"
        onClick={() => setIsOpen(!isOpen)}
        rightIcon={<FiChevronDown style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: '0.3s' }} />}
        color={linkColor}
        borderRadius="xl"
        py={6}
        fontWeight="bold"
      >
        {title}
      </Button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="accordion-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <VStack align="stretch" pl={4} pt={1} pb={2} ml={4} borderLeft="2px solid" borderColor={borderColor} spacing={1}>
              {items.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={closeAllMenus} isSubItem={true}>
                  {item.label}
                </NavLink>
              ))}
            </VStack>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

// --- Main Header Component ---
const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [burgerOpen, setBurgerOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const { colorMode, toggleColorMode } = useColorMode();

  const mobileMenuBg = useColorModeValue('white', 'gray.900');
  const separatorColor = useColorModeValue('gray.100', 'gray.700');
  const desktopNavBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(20, 35, 55, 0.85)');
  const desktopNavBorder = useColorModeValue('gray.200', 'gray.700');
  const mobileBarBg = useColorModeValue('rgba(255, 255, 255, 0.9)', 'rgba(26, 32, 44, 0.9)');
  const mobileBarBorder = useColorModeValue('gray.100', 'gray.700');
  const menuIconColor = useColorModeValue('#274559', 'white');
  const closeBtnBg = useColorModeValue('gray.100', 'gray.800');
  const drawerFooterBg = useColorModeValue('gray.50', 'gray.800');
  
  // Couleurs spécifiques pour le bouton déconnexion allégé
  const logoutColor = useColorModeValue('gray.500', 'gray.400');
  const logoutHoverBg = useColorModeValue('red.50', 'rgba(229, 62, 62, 0.15)');

  const isLoggedIn = !!localStorage.getItem('token');

  const toggleMenu = (menu) => setOpenMenu(openMenu === menu ? null : menu);
  const closeAllMenus = () => {
    setOpenMenu(null);
    setBurgerOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeAllMenus();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    closeAllMenus();
  };

  const prestationItems = [
    { to: "/enfants", label: "Garde d'enfants" },
    { to: "/seniors", label: "Aide aux personnes âgées" },
    { to: "/personnes-handicapees", label: "Accompagnement du handicap" },
    { to: "/transport-de-pmr", label: "Transport de PMR" },
    { to: "/entretien-du-domicile", label: "Entretien du domicile" },
  ];

  const tarifItems = [
    { to: "/demande-de-devis", label: "Demande de devis" },
    { to: "/tarifs", label: "Base tarifaire" },
    { to: "/aide-financiere", label: "Aide financière" },
  ];

  return (
   <Box ref={menuRef} position="fixed" top="0" left="0" zIndex="1000" w="full" px={4} py={4}>
      
      {/* Desktop Header: Floating Pill Style */}
      <Flex display={{ base: 'none', lg: 'flex' }} justifyContent="center">
        <HStack
          as="nav"
          bg={desktopNavBg}
          backdropFilter="blur(12px)"
          px={3} py={1.5}
          borderRadius="full"
          boxShadow="lg"
          border="1px solid"
          borderColor={desktopNavBorder}
          gap={1}
        >
          <Link as={RouterLink} to="/" onClick={closeAllMenus}>
            <motion.img src={Logo} alt="Logo" style={{ height: '35px' }} whileHover={{ scale: 1.05 }} />
          </Link>
          <Divider orientation="vertical" h="20px" mx={2} />
          
          <NavLink to="/agences">Agence</NavLink>

          <DropdownMenu title="Prestation" isOpen={openMenu === 'prestations'} onToggle={() => toggleMenu('prestations')}>
            {prestationItems.map(item => (
              <NavLink key={item.to} to={item.to} onClick={closeAllMenus} isSubItem={true}>{item.label}</NavLink>
            ))}
          </DropdownMenu>

          <DropdownMenu title="Tarif" isOpen={openMenu === 'tarif'} onToggle={() => toggleMenu('tarif')}>
            {tarifItems.map(item => (
              <NavLink key={item.to} to={item.to} onClick={closeAllMenus} isSubItem={true}>{item.label}</NavLink>
            ))}
          </DropdownMenu>

          {/* Modification du terme Certif -> Qualité */}
          <NavLink to="/agrements">Qualité</NavLink>
          
          <NavLink to="/blog">Blog</NavLink>
          <NavLink to="/journal">Journal</NavLink>

          <Divider orientation="vertical" h="20px" mx={2} />

          {/* Déconnexion allégé visuellement */}
          {isLoggedIn ? (
            <HStack spacing={1}>
              <NavLink to="/admin">Dashboard</NavLink>
              <Button
                color={logoutColor}
                _hover={{ color: 'red.500', bg: logoutHoverBg }}
                size="sm"
                variant="ghost"
                onClick={handleLogout}
                borderRadius="full"
              >
                Déconnexion
              </Button>
            </HStack>
          ) : (
            <NavLink to="/connexion">Connexion</NavLink>
          )}

          <Button onClick={toggleColorMode} variant="ghost" size="sm" borderRadius="full">
            {colorMode === 'light' ? <FiMoon /> : <FiSun />}
          </Button>

          {/* NOUVEAU CTA CONTACT : Mis en avant avec un fond plein */}
          <Link as={RouterLink} to="/contact" style={{ textDecoration: 'none', marginLeft: '8px' }} onClick={closeAllMenus}>
            <Button
              bg="blue.600"
              color="white"
              borderRadius="full"
              px={6}
              size="sm"
              fontWeight="bold"
              boxShadow="md"
              _hover={{ bg: 'blue.700', transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
            >
              Contact
            </Button>
          </Link>

        </HStack>
      </Flex>

      {/* Mobile Header: Sleek Bar */}
      <Flex
        display={{ base: 'flex', lg: 'none' }}
        alignItems="center"
        justifyContent="space-between"
        p={3}
        bg={mobileBarBg}
        backdropFilter="blur(10px)"
        borderRadius="2xl"
        boxShadow="sm"
        border="1px solid"
        borderColor={mobileBarBorder}
      >
        <Link as={RouterLink} to="/" onClick={closeAllMenus}>
          <img src={Logo} alt="Logo" style={{ height: '35px' }} />
        </Link>
        <Button onClick={() => setBurgerOpen(true)} variant="ghost" borderRadius="full" p={2}>
          <FiMenu size="26" color={menuIconColor} />
        </Button>
      </Flex>

      {/* Mobile Drawer Overlay & Slider */}
      <AnimatePresence>
        {burgerOpen && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeAllMenus}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(5px)',
              zIndex: 1999,
            }}
          />
        )}

        {burgerOpen && (
          <MotionBox
            key="mobile-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            position="fixed" top="0" right="0" bottom="0"
            width="85%" maxWidth="350px"
            bg={mobileMenuBg}
            borderLeftRadius="2xl"
            boxShadow="-10px 0 30px rgba(0,0,0,0.15)"
            zIndex={2000}
            display="flex"
            flexDirection="column"
          >
            <Flex justifyContent="space-between" alignItems="center" p={6} borderBottom="1px solid" borderColor={separatorColor}>
              <img src={Logo} alt="Logo" style={{ height: '30px' }} />
              <Button onClick={() => setBurgerOpen(false)} variant="ghost" borderRadius="full" bg={closeBtnBg}>
                <FiX size="20" />
              </Button>
            </Flex>
            
            <Box flex="1" overflowY="auto" p={4} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
              <VStack align="stretch" spacing={2}>
                <NavLink to="/agences" onClick={closeAllMenus}>Agence</NavLink>
                <AccordionMenu title="Prestation" items={prestationItems} closeAllMenus={closeAllMenus} />
                <AccordionMenu title="Tarif" items={tarifItems} closeAllMenus={closeAllMenus} />
                {/* Modification Certif -> Qualité */}
                <NavLink to="/agrements" onClick={closeAllMenus}>Qualité</NavLink>
                <NavLink to="/blog" onClick={closeAllMenus}>Le Blog</NavLink>
                <NavLink to="/journal" onClick={closeAllMenus}>Journal</NavLink>
              </VStack>
            </Box>

            <Box p={6} borderTop="1px solid" borderColor={separatorColor} bg={drawerFooterBg}>
              <VStack spacing={4}>
                
                {/* CTA Contact en Mobile (Tout en haut de la zone d'action, en bleu) */}
                <Button as={RouterLink} to="/contact" bg="blue.600" color="white" w="full" borderRadius="xl" _hover={{ bg: 'blue.700' }} onClick={closeAllMenus}>
                  Contact
                </Button>

                {isLoggedIn ? (
                  <VStack spacing={2} w="full">
                    <Button as={RouterLink} to="/admin" variant="outline" colorScheme="blue" w="full" borderRadius="xl" onClick={closeAllMenus}>
                      Dashboard
                    </Button>
                    <Button
                      variant="ghost"
                      color={logoutColor}
                      _hover={{ color: 'red.500', bg: logoutHoverBg }}
                      w="full"
                      borderRadius="xl"
                      onClick={handleLogout}
                    >
                      Déconnexion
                    </Button>
                  </VStack>
                ) : (
                  <Button as={RouterLink} to="/connexion" variant="outline" colorScheme="blue" w="full" borderRadius="xl" onClick={closeAllMenus}>
                    Espace Pro
                  </Button>
                )}
                
                <Button w="full" borderRadius="xl" onClick={toggleColorMode} leftIcon={colorMode === 'light' ? <FiMoon /> : <FiSun />} variant="outline">
                  Passer au mode {colorMode === 'light' ? 'Sombre' : 'Clair'}
                </Button>

              </VStack>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Header;